import { useEffect, useMemo, useState } from "react";
import { useGetSubscriptionsQuery } from "../../../features/api/subscriptionApiSlice";
import LoadingSpinner from "../../loader/Loader";
import { Link } from "react-router-dom";
import { useVideo } from "../../../hooks/useVideo";
import { generatePageNumbers } from "../../../util/generatePageNumber";

const SubscriptionList = () => {
  const { formatDate } = useVideo();

  // ✅ Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8; // categories per page

  const {
    data: subscriptions = [], // ✅ safe default so maps never blow up
    isFetching, // use this for spinner
    isError,
    isLoading,
    refetch,
  } = useGetSubscriptionsQuery(undefined, {
    refetchOnMountOrArgChange: "always", // ✅ refetch every time page mounts
    refetchOnFocus: true, // refresh when tab regains focus
    refetchOnReconnect: true, // refresh when network comes back
  });

  useEffect(() => {
    if (!subscriptions?.length && !isFetching && !isError) {
      refetch(); // one more nudge in case the first request was aborted in dev
    }
  }, [subscriptions?.length, isFetching, isError, refetch]);

  //   if (isLoading) return <LoadingSpinner />;
  const [userFilter, setUserFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");

  // Filtered transactions
  const filteredSubscriptions = useMemo(() => {
    if (!subscriptions) return [];
    return subscriptions.filter((tx) => {
      const matchesUser =
        !userFilter ||
        `${tx?.userId?.firstName} ${tx?.userId?.lastName}`
          .toLowerCase()
          .includes(userFilter.toLowerCase());
      const matchesPlan =
        !planFilter ||
        tx?.type.toLowerCase().includes(planFilter.toLowerCase());
      return matchesUser && matchesPlan;
    });
  }, [subscriptions, userFilter, planFilter]);

  const totalPages = Math.ceil(filteredSubscriptions.length / pageSize);

  const currentSubscriptions = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredSubscriptions.slice(startIdx, startIdx + pageSize);
  }, [filteredSubscriptions, currentPage, pageSize]);

  const pageNumbers = useMemo(
    () => generatePageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <p>Error loading transactions.</p>;

  return (
    <div className="transactions-container">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Subscriptions</h1>
        </div>
      </div>
      <div className="filters">
        <input
          type="text"
          placeholder="Filter by first or last name"
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
        />
        <input
          type="text"
          placeholder="Filter by plan type"
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
        />
      </div>

      <table className="transactions-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>User</th>
            <th>Plan</th>
            <th>Status</th>
            <th>End Date</th>
            <th>History</th>
          </tr>
        </thead>
        <tbody>
          {currentSubscriptions?.map((tx) => (
            <tr key={tx._id}>
              <td>{tx?._id}</td>
              <td>
                {tx.userId?.firstName} {tx.userId?.lastName}
              </td>
              <td>{tx?.type}</td>
              <td>{tx?.status}</td>
              <td>{formatDate(tx?.endDate)}</td>
              <td className="actions-container">
                <Link
                  className="ed-button"
                  aria-label={`edit ${tx?.type}`}
                  to={`/admin/subscriptions/details/${tx?._id}`}
                >
                  View History
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* ✅ Pagination */}
      {totalPages > 1 && (
        <div className="pagination responsive-pagination">
          <button
            onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
          >
            {"<"}
          </button>
          {pageNumbers.map((page, idx) =>
            page === "..." ? (
              <span className="ellipsis" key={idx}>
                ...
              </span>
            ) : (
              <button
                key={idx}
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() =>
              handlePageClick(Math.min(currentPage + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionList;
